'use client';

import React, { useContext } from 'react';
import { ErrorMessage, FormikContext } from 'formik';
import { tv } from 'tailwind-variants';
import { SvgIcon } from 'helpers/SvgIcon';

const formLabelStyles = tv({
  base: [
    'text-color-text-text',
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'font-semibold',
    'leading-typography-line-height-body-medium',
    'font-semibold',
  ],
  variants: {
    state: {
      default: '',
      error: 'text-color-text-text-critical',
      disabled: 'text-color-text-text-secondary',
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

const errorMessageStyles = tv({
  base: [
    'text-color-text-text-critical',
    'font-typography-font-family-body',
    'text-typography-font-size-body-small',
    'leading-[21px]',
    'font-normal',
  ],
});

const helpTextStyles = tv({
  base: [
    'text-color-text-text',
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'leading-[24px]',
    'font-normal',
  ],
});

const fieldWrapperStyles = tv({
  base: ['flex', 'flex-col', 'gap-spacing-spacing-12'],
  variants: {
    state: {
      default: '',
      error: '',
      disabled: 'opacity-40',
    },
  },
});

const errorMessageWrapperStyles = tv({
  base: ['flex', 'gap-spacing-spacing-4', 'items-center'],
});

interface FormFieldWrapperProps {
  fieldName: string;
  label: string;
  helpText?: string;
  children: React.ReactNode;
  hasError?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  hideLabel?: boolean;
  errors?: Record<string, string | undefined>;
  touched?: Record<string, boolean | undefined>;
}

export const FormFieldWrapper: React.FC<FormFieldWrapperProps> = ({
  fieldName,
  label,
  helpText,
  children,
  hasError = false,
  isDisabled = false,
  isRequired = false,
  hideLabel = false,
  errors: errorsProp,
  touched: touchedProp,
}) => {
  const labelState = hasError ? 'error' : isDisabled ? 'disabled' : 'default';
  // Use useContext instead of useFormikContext to avoid warnings when no Formik provider
  const formikContext = useContext(FormikContext);
  // Try to use Formik context if available, otherwise use props
  const errors = errorsProp || (formikContext?.errors as Record<string, string | undefined>);
  const touched = touchedProp || (formikContext?.touched as Record<string, boolean | undefined>);

  const renderLabelWithRequiredIndicator = (labelText: string) => (
    <>
      {labelText}
      {isRequired && <span>*</span>}
    </>
  );

  return (
    <div className={fieldWrapperStyles({ state: labelState })}>
      {!hideLabel && (
        <label htmlFor={fieldName} className={formLabelStyles({ state: labelState })}>
          {renderLabelWithRequiredIndicator(label)}
        </label>
      )}
      {children}
      {helpText && <small className={helpTextStyles()}>{helpText}</small>}
      {errors?.[fieldName] && touched?.[fieldName] && (
        <div className={errorMessageWrapperStyles()}>
          <SvgIcon
            icon="form-error"
            className={'text-color-text-text-critical'}
            size="xs"
            viewBox="0 0 16 17"
          />
          {formikContext ? (
            <ErrorMessage name={fieldName} component="div" className={errorMessageStyles()} />
          ) : (
            <div className={errorMessageStyles()}>{errors[fieldName]}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default FormFieldWrapper;
